/**
 * Matomo Analytics Service
 * 
 * This service provides methods to interact with Matomo Analytics.
 * It handles initialization, page tracking, event tracking, and other Matomo-specific functionality.
 */

// Define the window interface to include Matomo's _paq array
declare global {
  interface Window {
    _paq: any[];
  }
}

class MatomoService {
  private initialized: boolean = false;
  private matomoUrl: string;
  private siteId: number;

  /**
   * Initialize the Matomo service with configuration
   * 
   * @param matomoUrl - URL to your Matomo instance (without trailing slash)
   * @param siteId - The site ID configured in Matomo
   */
  constructor(matomoUrl: string = '', siteId: number = 1) {
    this.matomoUrl = matomoUrl;
    this.siteId = siteId;
  }

  /**
   * Initialize Matomo tracking code
   * Should be called once when the application starts
   */
  init(matomoUrl?: string, siteId?: number): void {
    if (this.initialized) {
      console.warn('Matomo already initialized');
      return;
    }

    // Use provided values or fall back to constructor values
    const url = matomoUrl || this.matomoUrl;
    const id = siteId || this.siteId;

    if (!url) {
      console.error('Matomo URL is required for initialization');
      return;
    }

    // Initialize the _paq array if it doesn't exist
    window._paq = window._paq || [];

    // Set up basic tracking
    window._paq.push(['enableLinkTracking']);
    window._paq.push(['setTrackerUrl', `${url}/matomo.php`]);
    window._paq.push(['setSiteId', id]);

    // Create and append Matomo script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    // Use the CDN URL for Matomo cloud
    script.src = `https://cdn.matomo.cloud/${url.replace('https://', '')}/matomo.js`;
    
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
      this.initialized = true;
      console.log('Matomo initialized successfully');
    } else {
      console.error('Failed to initialize Matomo: No script element found');
    }
  }

  /**
   * Track a page view
   * 
   * @param customTitle - Optional custom page title
   */
  trackPageView(customTitle?: string): void {
    if (!this.initialized) {
      console.warn('Matomo not initialized. Call init() first.');
      return;
    }

    if (customTitle) {
      window._paq.push(['setDocumentTitle', customTitle]);
    }
    window._paq.push(['trackPageView']);
  }

  /**
   * Track an event
   * 
   * @param category - Event category
   * @param action - Event action
   * @param name - Optional event name
   * @param value - Optional event value
   */
  trackEvent(category: string, action: string, name?: string, value?: number): void {
    if (!this.initialized) {
      console.warn('Matomo not initialized. Call init() first.');
      return;
    }

    const eventData = ['trackEvent', category, action];
    
    if (name !== undefined) {
      eventData.push(name);
      
      if (value !== undefined) {
        eventData.push(value);
      }
    }
    
    window._paq.push(eventData);
  }

  /**
   * Set a custom dimension
   * 
   * @param dimensionId - The dimension ID (configured in Matomo)
   * @param value - The dimension value
   */
  setCustomDimension(dimensionId: number, value: string): void {
    if (!this.initialized) {
      console.warn('Matomo not initialized. Call init() first.');
      return;
    }

    window._paq.push(['setCustomDimension', dimensionId, value.toString()]);
  }

  /**
   * Track a site search
   * 
   * @param keyword - Search keyword
   * @param category - Optional search category
   * @param resultsCount - Optional number of search results
   */
  trackSiteSearch(keyword: string, category?: string, resultsCount?: number): void {
    if (!this.initialized) {
      console.warn('Matomo not initialized. Call init() first.');
      return;
    }

    window._paq.push(['trackSiteSearch', keyword, category || false, resultsCount === undefined ? undefined : resultsCount]);
  }

  /**
   * Track an outlink
   * 
   * @param url - URL that was clicked
   */
  trackLink(url: string): void {
    if (!this.initialized) {
      console.warn('Matomo not initialized. Call init() first.');
      return;
    }

    window._paq.push(['trackLink', url, 'link']);
  }

  /**
   * Set the user ID for cross-device user tracking
   * 
   * @param userId - Unique, non-empty string that identifies the user
   */
  setUserId(userId: string): void {
    if (!userId) {
      console.warn('User ID cannot be empty');
      return;
    }

    window._paq.push(['setUserId', userId.toString()]);
  }

  /**
   * Reset the user ID and associated data
   */
  resetUserId(): void {
    window._paq.push(['resetUserId']);
  }
}

// Create and export a singleton instance
const matomoService = new MatomoService();
export default matomoService;
